import { Headphones, PiggyBank, ShieldCheck, Timer } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { registerSiteContent } from '@/features/siteContent/runtime';

/**
 * Everything the marketing home page says.
 *
 * The page's components carry layout and nothing else, so a copy change is a
 * change to this file alone. Anything here that a running business would one
 * day answer for itself - plan prices, merchant numbers, what customers said -
 * is marked `FASTAPI-STUB` with the endpoint that would replace it.
 *
 * Every `to` in this file points at a route that exists in `routes/router.tsx`.
 * The reference design also showed a blog, a help centre, guides, careers and
 * success stories; none of those pages are built, so rather than ship links
 * that land on the 404 page they are left out until there is somewhere for
 * them to go.
 */

export interface HomeHighlight {
  title: string;
  description: string;
}

export const HIGHLIGHTS_SECTION = {
  /** The band carries no visible heading, so this names it for a screen reader. */
  label: 'What running a store on NeonCarts is like',
};

export const HOME_HIGHLIGHTS: HomeHighlight[] = [
  {
    title: 'Make your brand impossible to miss',
    description:
      'Pick a theme, set your colours and drop in your logo, and end up with a storefront that looks like your shop - not like a template a hundred other sellers are also using.',
  },
  {
    title: 'Go live without writing a line',
    description:
      'No developer, no agency, no waiting on a quote. Choose a domain, add your products and open the doors. It really is as short a list as it looks.',
  },
  {
    title: 'Own the checkout, end to end',
    description:
      'Show buyers how quick and safe paying you is - UPI, cards or cash on delivery - with the GST invoice raised the moment an order lands.',
  },
  {
    title: 'Arrange your catalogue with care',
    description:
      'Group products into collections, sort them the way your customers actually shop, and keep everything worth buying a single tap from the front page.',
  },
];

export interface FeatureCard {
  /** 3D icon from `public/icons`, square. */
  iconSrc: string;
  title: string;
  description: string;
  /** Slug of the `/features/:slug` page the card opens. */
  slug: string;
}

export const FEATURES_SECTION = {
  heading: 'All the tools you need to grow, in one place',
  subheading: 'Powerful features. Simple to use. Built for every Indian business.',
  linkLabel: 'Explore all features',
  linkTo: '/why-shoplaunch',
};

export const FEATURE_CARDS: FeatureCard[] = [
  {
    iconSrc: '/icons/OnlineStore.png',
    title: 'Online store',
    description: 'Beautiful themes & custom domains to build your brand online.',
    slug: 'online-store',
  },
  {
    iconSrc: '/icons/productsInventory.png',
    title: 'Products & inventory',
    description: 'Manage products, variants, stock and inventory effortlessly.',
    slug: 'inventory',
  },
  {
    iconSrc: '/icons/orderShipping.png',
    title: 'Orders & shipping',
    description: 'Process orders, print labels and ship with top courier partners.',
    slug: 'orders',
  },
  {
    iconSrc: '/icons/whatsAppBusiness.png',
    title: 'WhatsApp business',
    description: 'Engage customers, send updates and sell directly on WhatsApp.',
    slug: 'whatsapp-commerce',
  },
  {
    iconSrc: '/icons/AIAssistant.png',
    title: 'AI assistant',
    description: 'Let AI write product descriptions, create images and help you grow faster.',
    slug: 'ai-assistant',
  },
  {
    iconSrc: '/icons/Analytics.png',
    title: 'Analytics',
    description: 'Real-time insights to understand your business and make better decisions.',
    slug: 'analytics',
  },
];

export interface Industry {
  /** Cutout under `public/industries` - transparent PNG, shown whole. */
  image: string;
  label: string;
}

export const INDUSTRIES_SECTION = {
  heading: 'Built for every industry',
  subheading:
    'From local shops to large brands - NeonCarts powers businesses across all sectors.',
  /** Names the rail for screen readers, and labels its scroll controls. */
  railLabel: 'Industries NeonCarts serves',
};

export const INDUSTRIES: Industry[] = [
  { image: '/industries/Fashion.png', label: 'Fashion & Apparel' },
  { image: '/industries/Grocery.png', label: 'Grocery & FMCG' },
  { image: '/industries/Electronics.png', label: 'Electronics & Gadgets' },
  { image: '/industries/Beauty.png', label: 'Beauty & Personal Care' },
  { image: '/industries/Home.png', label: 'Home & Lifestyle' },
  { image: '/industries/Food.png', label: 'Food & Beverages' },
  { image: '/industries/Health.png', label: 'Health & Wellness' },
  { image: '/industries/Jewellery.png', label: 'Jewellery & Accessories' },
  { image: '/industries/Books.png', label: 'Books & Stationery' },
  { image: '/industries/AndMany.png', label: 'And many more' },
];

export interface WhyPoint {
  /** `tricolour` draws the India roundel instead of a lucide glyph. */
  icon: LucideIcon | 'tricolour';
  title: string;
  description: string;
}

export const WHY_SECTION = { heading: 'Why businesses choose NeonCarts' };

export const WHY_POINTS: WhyPoint[] = [
  {
    icon: 'tricolour',
    title: 'Made for India',
    description: 'With GST, UPI and local business needs in mind.',
  },
  {
    icon: PiggyBank,
    title: 'Affordable & transparent',
    description: 'No hidden fees. Plans for every stage.',
  },
  {
    icon: ShieldCheck,
    title: 'Secure & reliable',
    description: 'Bank-grade security and 99.9% uptime.',
  },
  {
    icon: Headphones,
    title: 'Expert support',
    description: 'Real people. Real help. Whenever you need it.',
  },
];

// How quickly a store goes live, not how many merchants there are: a merchant
// count is only worth showing once it is real. When it is, a public, rounded
// version of the platform's own count is what would go here.
export const WHY_HIGHLIGHT = {
  icon: Timer,
  value: '10 min',
  caption: 'From sign-up to a live store - no code, no agency',
};

export interface QuickInstallStep {
  /** One word - the whole step, for someone who reads only that. */
  title: string;
  /** A few words under it. */
  description: string;
  /** What "Listen" says aloud for this step, as one plain sentence. */
  say: string;
  /** What the phone shows during this step, for a screen reader. */
  sceneLabel: string;
}

/** A product picture from the organic & natural library, at its stored (widest) rung. */
function harvestJarProduct(id: string) {
  return `https://d2vky71drg0d8r.cloudfront.net/product-images/platform/industry-assets/organic_natural_products/product/${id}/1600.webp`;
}

/**
 * "Quick install": a merchant's storefront goes onto a shopper's phone like an
 * app, from one scan of the store QR.
 *
 * Grounded in `storefront/pwa/installPrompt.ts` - a shopper who arrives from
 * the printed code (`source=qr`) is offered the install, Android browsers
 * show their own dialog and iPhone users are pointed at Add to Home Screen -
 * and `store/pages/StoreQrCodePage.tsx`, where the code comes from. Nothing
 * here promises an app-store listing, offline use or push notifications,
 * because the storefront has none of those.
 *
 * Written for a shopkeeper who reads slowly, if at all: one word per step, a
 * big number and a colour for each, a phone that acts each step out, and a
 * "Listen" button that reads the lot aloud. The install is the
 * `INSTALLABLE_STOREFRONT` plan feature, so the copy never says "every plan" -
 * the pricing cards say which plans carry it.
 */
export const QUICK_INSTALL_SECTION = {
  eyebrow: 'QuickInstall',
  heading: 'Your shop, an app on every phone.',
  /** Set on its own line under the heading, in the highlight colour. */
  headingAccent: 'Scan. Tap. Shop.',
  subheading: 'Customers scan your QR code. Your shop sits on their phone like an app.',
  steps: [
    {
      title: 'Scan',
      description: 'Camera on your QR',
      say: 'One. Your customer points the phone camera at your QR code.',
      sceneLabel: 'A phone camera pointed at the shop QR code',
    },
    {
      title: 'Tap',
      description: 'One tap to install',
      say: 'Two. They tap Install, just once.',
      sceneLabel: 'A finger tapping the Install button on the shop page',
    },
    {
      title: 'Shop',
      description: 'Opens like any app',
      say: 'Three. Your shop is now an app on their phone, next to WhatsApp.',
      sceneLabel: 'The shop installed as an app on the home screen beside WhatsApp',
    },
  ] satisfies QuickInstallStep[],
  /** Read aloud before the steps, and after them. */
  sayIntro: 'Your shop can become an app on your customer\'s phone, in three steps.',
  sayOutro: 'No app store. No big download.',
  /** Small icon-led promises under the steps - two words each. */
  perks: ['No app store', 'Android & iPhone', 'Free for customers'],
  /** The read-aloud button, and the playback control for the story. */
  listenLabel: 'Listen',
  stopListeningLabel: 'Stop',
  pauseLabel: 'Pause the steps',
  playLabel: 'Play the steps',
  /** Under the steps: which plans carry it, so nobody on the entry plan is surprised. */
  planNote: 'Comes with our higher plans.',
  cta: { label: 'See how it works', to: '/features/share-links' },
  secondaryCta: { label: 'Create your store', to: '/register' },
  /** The badge that floats above the phone. */
  badge: 'No app store',
  /** The sample shop drawn on the counter card and installed on the phone. */
  demoShop: {
    name: 'Aurelle Store',
    logo: '/images/Home/aurelle-store-logo.png',
    /**
     * The catalogue behind the install sheet in step 2: real products from
     * the organic & natural library the Harvest Jar theme is built on (see
     * `docs/theme-redesigns/harvest_jar_design.py`), so the drawn shop looks
     * like a shop rather than four coloured boxes.
     */
    products: [
      { name: 'Herbal tea', price: '₹249', image: harvestJarProduct('4f613c86cc7f43f0abbb24eb561f7bc8') },
      { name: 'Roasted makhana', price: '₹199', image: harvestJarProduct('11343b4a3e96424abca9f2586fc9904a') },
      { name: 'Cold-pressed oil', price: '₹349', image: harvestJarProduct('e3bc4ca9e2844adcafb3730d146ea3cd') },
      { name: 'Handmade soap', price: '₹129', image: harvestJarProduct('cd8ff4a0812a47d7bec5c2adaf2d83e2') },
    ],
  },
};

export interface LaunchStep {
  /**
   * The card's artwork, which fills the card behind the copy. Each one is
   * drawn with the step's own glyph already badged into its top-left corner,
   * so the card no longer draws a lucide icon of its own.
   */
  image: string;
  step: string;
  title: string;
  description: string;
}

export const STEPS_SECTION = {
  heading: 'Launch your store in three simple steps',
  subheading: 'No agencies. No upfront cost. Just your ideas and our platform.',
};

export const LAUNCH_STEPS: LaunchStep[] = [
  {
    image: '/images/Home/launch/createStore.png',
    step: '01',
    title: 'Create your store',
    description: 'Sign up, choose a theme and set your brand details.',
  },
  {
    image: '/images/Home/launch/addProduct.png',
    step: '02',
    title: 'Add your products',
    description: 'Upload photos, set prices and manage inventory.',
  },
  {
    image: '/images/Home/launch/Share.png',
    step: '03',
    title: 'Share and sell',
    description: 'Go live, share on WhatsApp, Instagram or any channel.',
  },
];

export interface PricingTeaser {
  /** What this rung of the ladder is called until the plan behind it is read. */
  name: string;
  tagline: string;
  /**
   * Written here only for the quoted card, which has no plan row behind it to
   * read. Every other card takes its price from the catalogue, and shows
   * nothing at all rather than a figure typed into this file.
   */
  price?: string;
  /** Empty for a plan that is quoted rather than listed. */
  period?: string;
  popular: boolean;
  /**
   * Whether this rung includes the installable shop (`INSTALLABLE_STOREFRONT`).
   * Only a stand-in until the catalogue answers: a published plan's own grant
   * is drawn over it, so the card can never claim what the plan does not sell.
   */
  mobileApp: boolean;
  cta: { label: string; to: string };
}

export const PRICING_SECTION = {
  heading: 'Flexible pricing for every stage',
  subheading: 'Start free and upgrade as you grow.',
  /** Said instead when no plan on sale is free, because then the line above is not true. */
  paidSubheading: 'Pick the plan that fits today and move up as you grow.',
  popularLabel: 'Most popular',
  /** Beside a published plan's per-month price. */
  periodLabel: '/month',
  /** The one feature each card answers yes or no to: the QuickInstall app. */
  mobileAppLabel: 'Mobile app included',
  mobileAppMissing: 'No mobile app',
};

// The published plans are what `PricingPanel` shows: it reads the same
// catalogue `/pricing` renders, which the platform owner edits at
// `/platform/plans`. What is left here is the copy around them - what each rung
// of the ladder is for, and the quoted card, which has no plan row behind it.
//
// No price for the first two, on purpose. They used to carry one, and the
// figures were a third of what those plans actually cost by the time anybody
// looked: a marketing page keeping its own copy of the prices goes stale the
// first time somebody changes one. The panel shows a placeholder until the
// catalogue answers and nothing if it never does, which is worse-looking than
// a number and better than the wrong number.
//
// The names are the same kind of stand-in: replaced by the plan's own name the
// moment there is one, and never attached to a price.
export const PRICING_TEASERS: PricingTeaser[] = [
  {
    name: 'Starter',
    tagline: 'Start and explore',
    popular: false,
    mobileApp: false,
    cta: { label: 'Get started', to: '/register' },
  },
  {
    name: 'Growth',
    tagline: 'For growing businesses',
    popular: true,
    mobileApp: true,
    cta: { label: 'View plans', to: '/pricing' },
  },
  {
    name: 'Enterprise',
    tagline: 'For large businesses',
    price: 'Custom',
    period: '',
    popular: false,
    mobileApp: true,
    cta: { label: 'Talk to us', to: '/enterprise' },
  },
];

export interface Testimonial {
  quote: string;
  name: string;
  business: string;
  /** Out of five, drawn as filled stars. */
  rating: number;
}

export const TESTIMONIALS_SECTION = {
  heading: 'What our merchants say',
  railLabel: 'Merchant testimonials',
  previousLabel: 'Previous testimonial',
  nextLabel: 'Next testimonial',
};

// FASTAPI-STUB: hand-written placeholders. Real quotes would come from a
// public testimonials endpoint so they can be changed without a deploy.
export const TESTIMONIALS: Testimonial[] = [
  {
    quote:
      'NeonCarts made it so easy for me to take my boutique online. My sales have grown 3x in just 3 months!',
    name: 'Priya Sharma',
    business: 'The Ethnic House, Jaipur',
    rating: 5,
  },
  {
    quote:
      'The WhatsApp integration is a game changer. I can now manage orders and customers in one place.',
    name: 'Rahul Mehta',
    business: 'FreshKart, Delhi',
    rating: 5,
  },
  {
    quote: 'Simple, affordable and powerful. Exactly what Indian small businesses need.',
    name: 'Sneha Verma',
    business: 'Glow & Co, Mumbai',
    rating: 5,
  },
];

export interface HomeFaq {
  question: string;
  answer: string;
}

export const FAQ_SECTION = {
  heading: 'Frequently asked questions',
  linkLabel: 'View all FAQs',
  /** `/pricing` carries the long-form FAQ, built from the plans on sale. */
  linkTo: '/pricing',
};

export const HOME_FAQS: HomeFaq[] = [
  {
    question: 'How long does it take to launch my store?',
    answer:
      'About 10 minutes. Create an account, add your first products and your storefront is live on your own NeonCarts subdomain.',
  },
  {
    question: 'Do I need any technical skills?',
    answer:
      'No. Products, orders, discounts and invoices are all managed from one simple dashboard. If you can use WhatsApp, you can run a NeonCarts store.',
  },
  {
    question: 'Which payment methods can my customers use?',
    answer:
      'Razorpay-powered UPI, cards and netbanking, plus cash on delivery. Totals are computed server-side and every order gets a GST-compliant invoice.',
  },
  {
    question: 'Is GST invoicing supported?',
    answer:
      'Yes. Add your GSTIN once and every order produces a compliant invoice with the right tax split, ready to download or send to the customer.',
  },
  {
    question: 'Can I sell on WhatsApp and Instagram?',
    answer:
      'Yes. Connect the official WhatsApp Business API to take orders and send updates in chat, and share your catalogue or a single product to Instagram with a link or a QR code.',
  },
];

export const CTA_BAND = {
  blurb: 'Join Indian businesses building their future online.',
  heading: 'Start selling online today.',
  ticks: ['No credit card required', 'Setup in 10 minutes', 'Cancel anytime'],
  cta: { label: 'Create your store', to: '/register' },
};

registerSiteContent('page.home', { HIGHLIGHTS_SECTION, HOME_HIGHLIGHTS, FEATURES_SECTION, FEATURE_CARDS, INDUSTRIES_SECTION, INDUSTRIES, WHY_SECTION, WHY_POINTS, WHY_HIGHLIGHT, QUICK_INSTALL_SECTION, STEPS_SECTION, LAUNCH_STEPS, PRICING_SECTION, PRICING_TEASERS, TESTIMONIALS_SECTION, TESTIMONIALS, FAQ_SECTION, HOME_FAQS, CTA_BAND });
